import { readStoredUserId } from './authTokens';
import { callParkingBoxApi } from './parkingBoxApi';
import { normalizeVehicleLicensePlate } from './vehicles';

export type AddParkingSessionPayload = {
  plate: string;
  parkingId: string | number;
  userId: string | number;
  startTime?: string | null;
  stopTime?: string | null;
};

export type ModifyParkingSessionPayload = {
  sessionId: string | number;
  stopTime: string;
};

export type NormalizedParkingSessionApiResponse = {
  status: number | null;
  message: string | null;
  sessionId: string | null;
};

const normalizeParkingSessionString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeParkingSessionNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return Math.floor(parsed);
    }
  }

  return null;
};

const normalizeParkingSessionId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return normalizeParkingSessionString(value);
};

const normalizeParkingSessionRequestId = (value: string | number, fieldName: string): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeParkingSessionString(value);
  if (normalized !== null) {
    return normalized;
  }

  throw new Error(`Brak poprawnego ${fieldName} dla sesji parkingowej.`);
};

const normalizeParkingSessionPlate = (value: string): string => {
  const normalized = normalizeVehicleLicensePlate(value);
  if (!normalized.length) {
    throw new Error('Brak poprawnego numeru rejestracyjnego dla sesji parkingowej.');
  }

  return normalized;
};

const normalizeParkingSessionOwnerId = (value: string | number): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeParkingSessionString(value);
  if (normalized !== null) {
    return normalized;
  }

  throw new Error('Brak poprawnego user_id dla sesji parkingowej.');
};

const normalizeParkingId = (value: string | number): string | number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeParkingSessionString(value);
  if (normalized !== null) {
    return normalized;
  }

  throw new Error('Brak poprawnego parking_id dla sesji parkingowej.');
};

const isBackendParkingSessionDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value);

const formatParkingSessionDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  const seconds = String(value.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const normalizeParkingSessionDate = (value: string | null | undefined, fieldName: string): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = normalizeParkingSessionString(value);
  if (!normalized) {
    throw new Error(`Brak poprawnej daty pola ${fieldName}.`);
  }

  if (isBackendParkingSessionDate(normalized)) {
    return normalized;
  }

  const timestamp = Date.parse(normalized);
  if (!Number.isFinite(timestamp)) {
    throw new Error(`Niepoprawny format daty pola ${fieldName}.`);
  }

  return formatParkingSessionDate(new Date(timestamp));
};

const normalizeParkingSessionOptionalStopTime = (value: string | null | undefined): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return '';
  }

  return normalizeParkingSessionDate(value, 'stop_time');
};

const readSessionIdFromRecord = (record: Record<string, unknown>): string | null =>
  normalizeParkingSessionId(record.session_id ?? record.sessionId ?? record.sessionid ?? record.id);

export const normalizeParkingSessionApiResponse = (payload: unknown): NormalizedParkingSessionApiResponse => {
  if (!payload || typeof payload !== 'object') {
    return {
      status: null,
      message: null,
      sessionId: null,
    };
  }

  const record = payload as Record<string, unknown>;
  const directStatus = normalizeParkingSessionNumber(record.status);
  const directMessage = normalizeParkingSessionString(record.message);
  const directSessionId = readSessionIdFromRecord(record);
  const nestedSources = [record.data, record._data_];

  for (const nestedSource of nestedSources) {
    if (!nestedSource || typeof nestedSource !== 'object') {
      continue;
    }

    const nestedRecord = nestedSource as Record<string, unknown>;
    const nestedStatus = normalizeParkingSessionNumber(nestedRecord.status);
    const nestedMessage = normalizeParkingSessionString(nestedRecord.message);
    const nestedSessionId = readSessionIdFromRecord(nestedRecord);

    if (
      directStatus !== null ||
      directMessage !== null ||
      directSessionId !== null ||
      nestedStatus !== null ||
      nestedMessage !== null ||
      nestedSessionId !== null
    ) {
      return {
        status: directStatus ?? nestedStatus,
        message: directMessage ?? nestedMessage,
        sessionId: directSessionId ?? nestedSessionId,
      };
    }
  }

  return {
    status: directStatus,
    message: directMessage,
    sessionId: directSessionId,
  };
};

export const addParkingSession = async (payload: AddParkingSessionPayload) => {
  const startTime = normalizeParkingSessionDate(payload.startTime, 'start_time');
  const stopTime = normalizeParkingSessionOptionalStopTime(payload.stopTime);

  return callParkingBoxApi(
    {
      action: 'add-parking-session',
      _data_: {
        plate: normalizeParkingSessionPlate(payload.plate),
        user_id: normalizeParkingSessionOwnerId(payload.userId),
        parking_id: normalizeParkingId(payload.parkingId),
        ...(startTime ? { start_time: startTime } : {}),
        ...(stopTime !== undefined ? { stop_time: stopTime } : {}),
      },
    },
    {
      useStoredToken: true,
    },
  );
};

export const addParkingSessionForCurrentUser = async (
  payload: Omit<AddParkingSessionPayload, 'userId'>,
) => {
  const userId = readStoredUserId();
  if (!userId) {
    throw new Error('Brak user_id dla aktualnie zalogowanego uzytkownika.');
  }

  return addParkingSession({
    ...payload,
    userId,
  });
};

export const modifyParkingSession = async (payload: ModifyParkingSessionPayload) => {
  const stopTime = normalizeParkingSessionDate(payload.stopTime, 'stop_time');
  if (!stopTime) {
    throw new Error('Brak poprawnego stop_time dla modyfikacji sesji parkingowej.');
  }

  return callParkingBoxApi(
    {
      action: 'modify-parking-session',
      _data_: {
        session_id: normalizeParkingSessionRequestId(payload.sessionId, 'session_id'),
        stop_time: stopTime,
      },
    },
    {
      useStoredToken: true,
    },
  );
};

export const modifyParkingSessionForCurrentUser = async (payload: ModifyParkingSessionPayload) =>
  modifyParkingSession(payload);
